import get from "lodash/get";
import isEqual from "lodash/isEqual";
import moment from "moment";

import {
  ARCHIVE_STATUS_CHILDREN,
  ARCHIVE_STATUS_ENTRY,
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_FOLDER,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_ENTRY_WEBPAGE,
  MEDIA_LIBRARY_URL,
  VALID_IMAGE_MIMETYPES,
  VALID_MIMETYPES,
  VALID_VIDEO_MIMETYPES,
} from "cortex/ui/src/fleet/constant/mediaLibrary";
import type { Application } from "cortex/ui/src/fleet/view/application/model";
import type {
  ContentGroupItem,
  FriendlyType,
  MediaLibraryStoreContentFolder,
  MediaLibraryStoreContentGroup,
} from "cortex/ui/src/fleet/view/media-library/model";
import * as App from "cortex/ui/src/redux-store/applications/selectors";
import type { ById } from "cortex/ui/src/redux-store/reducers";
import { getById } from "cortex/ui/src/redux-store/selectors";
import { priorityMerge } from "cortex/ui/src/util/object";
import { utcToLocal } from "cortex/ui/src/util/time";

type ApplicationsStore = {
  allApps?: ById<Application>;
  byId?: ById<Application>;
};

type MediaStoreSlice<T> = {
  byId?: ById<T>;
};

type MediaStores = {
  applications: ApplicationsStore;
  contentFolders: MediaStoreSlice<MediaLibraryStoreContentFolder>;
  contentGroups: MediaStoreSlice<MediaLibraryStoreContentGroup>;
  userContents: MediaStoreSlice<ContentGroupItem>;
};

type CreativeCategoryContent = {
  archived?: boolean | null;
  tag?: string | null;
};

type CreativeCategorySpot = {
  conflicts_with?: string | null;
  options?: ReadonlyArray<{
    conflicts_with?: string | null;
  }>;
};

type CreativeCategoryStrategy = {
  strategy?: {
    priorities?: ReadonlyArray<ReadonlyArray<CreativeCategorySpot>>;
  };
};

type MediaEntry = {
  _rowNum: number | null;
  account_id: number | null;
  archived: boolean | null;
  available: boolean | null;
  conflictsWith: ReadonlyArray<string> | null;
  content: ReadonlyArray<MediaEntry> | null;
  content_groups: ReadonlyArray<MediaLibraryStoreContentGroup> | null;
  contents: ReadonlyArray<
    ContentGroupItem | MediaLibraryStoreContentGroup
  > | null;
  create_date: string | null;
  displayName: string | null;
  duration: number | null;
  folder_id: number | null;
  folderName: string | null;
  friendlyType: FriendlyType | "Unknown" | null;
  id: number | null;
  installed: boolean | null;
  mimeType: string | null;
  name: string | null;
  numberOfSpots: number | null;
  tag: string | null;
  type: string | null;
  upload_date: string | null;
  url: string | null;
  user_id: number | null;
};

type MediaEntryDraft = Partial<MediaEntry>;
type MediaEntryList = ReadonlyArray<MediaEntry>;

const MEDIA_ENTRY_SHAPE: MediaEntry = Object.freeze({
  _rowNum: null,
  account_id: null,
  archived: null,
  available: null,
  conflictsWith: null,
  content: null,
  content_groups: null,
  contents: null,
  create_date: null,
  displayName: null,
  duration: null,
  folder_id: null,
  folderName: null,
  friendlyType: null,
  id: null,
  installed: null,
  mimeType: null,
  name: null,
  numberOfSpots: null,
  tag: null,
  type: null,
  upload_date: null,
  url: null,
  user_id: null,
});

function createMediaEntry(props: MediaEntryDraft): MediaEntry {
  return priorityMerge({}, MEDIA_ENTRY_SHAPE, props) as MediaEntry;
}

function getAllByIdAsArray<T extends { id: string | number }>(
  byId: ById<T>,
): ReadonlyArray<T> {
  return Object.values(byId).sort((a, b) => Number(b.id) - Number(a.id));
}

function isUserContent(type: string | null | undefined): boolean {
  return type === MEDIA_ENTRY_USER_CONTENT;
}

function isImage(mimeType: string | null | undefined): boolean {
  return mimeType ? VALID_IMAGE_MIMETYPES.includes(mimeType) : false;
}

function isVideo(mimeType: string | null | undefined): boolean {
  return mimeType ? VALID_VIDEO_MIMETYPES.includes(mimeType) : false;
}

function isValidMimeType(mimeType: string | null | undefined): boolean {
  return mimeType ? VALID_MIMETYPES.includes(mimeType) : false;
}

function isContentFolder(type: string | null | undefined): boolean {
  return type === MEDIA_ENTRY_FOLDER;
}

function getFolderUrl(folderId: string | number): string {
  return `${MEDIA_LIBRARY_URL}/folders/${folderId}`;
}

function formatUploadDate(_props: unknown, media: MediaEntry): string {
  const date = media.upload_date;
  return utcToLocal(date);
}

function formatNameWithFolderName(
  name: string | null,
  mediaEntry: {
    folderName?: string | null;
    type?: string | null;
  },
): string | null {
  const { folderName, type } = mediaEntry;

  if (name && !isContentFolder(type) && folderName) {
    return `${folderName}/${name}`;
  }

  return name;
}

function getCreativeCategories(
  userContents: ReadonlyArray<CreativeCategoryContent> = [],
  strategies: ReadonlyArray<CreativeCategoryStrategy> = [],
): ReadonlyArray<string> {
  const categories = new Set<string>();
  userContents.forEach((c) => {
    if (c.tag && !c.archived) {
      categories.add(c.tag);
    }
  });

  const spots: CreativeCategorySpot[] = [];
  strategies.forEach((s) => {
    const priorities = get(s, "strategy.priorities", []) as ReadonlyArray<
      ReadonlyArray<CreativeCategorySpot>
    >;
    priorities.forEach((priority) => {
      spots.push(...priority);
    });
  });

  spots.forEach((spot) => {
    const { options = [] } = spot;
    let allConflictsWith = spot.conflicts_with || "";

    // Get categories from content option conflicts with
    if (options.length) {
      allConflictsWith = options
        .map((o) => {
          return o.conflicts_with || "";
        })
        .join(",");
    }

    allConflictsWith
      .split(",")
      .filter((cwCat) => cwCat)
      .forEach((cwc) => {
        categories.add(cwc);
      });
  });

  const { lastResult } = getCreativeCategories;
  const nextCategories = [...categories].sort();

  if (isEqual(lastResult, nextCategories)) {
    return lastResult;
  }

  getCreativeCategories.lastResult = nextCategories;
  return nextCategories;
}

namespace getCreativeCategories {
  export let lastResult: ReadonlyArray<string> = [];
}

/**
 * Takes arrays of normalized data and transforms them into an array
 * of MediaEntries.
 *
 * @typedef {Object} MediaEntry
 *
 * @param {Object} stores - an object containing stores:
 *   applications{}, contentGroups{}, folders{}, userContents{}.
 * @param {Array} sources - An array of exactly
 *   [applications[], contentGroups[], folders[], userContents[]]
 *
 * @return {MediaEntry[]} - an array of media entries.
 */
function getMediaEntries(
  stores: MediaStores,
  sources: [
    ReadonlyArray<Application>,
    ReadonlyArray<MediaLibraryStoreContentGroup>,
    ReadonlyArray<MediaLibraryStoreContentFolder>,
    ReadonlyArray<ContentGroupItem>,
  ],
): MediaEntryList {
  const [applications, contentGroups, folders, userContents] = sources;

  const dnApplications = applications
    .filter(App.installed)
    .map(applicationToMediaEntry);
  const dnUserContents = userContents.map(
    userContentToMediaEntry.bind(null, stores),
  );
  const dnContentGroups = contentGroups.map(
    contentGroupToMediaEntry.bind(null, stores),
  );
  const dnFolders = folders.map(contentFolderToMediaEntry);

  return [
    ...dnApplications,
    ...dnUserContents,
    ...dnContentGroups,
    ...dnFolders,
  ];
}

function sortByUploadDate(
  a: string | moment.Moment,
  b: string | moment.Moment,
  order: "asc" | "desc",
): number {
  const aDate = moment(a);
  const bDate = moment(b);
  if (aDate.isSame(bDate)) {
    return 0;
  }

  const dir = order === "desc" ? -1 : 1;
  const sortOrder = aDate > bDate ? 1 : -1;
  return dir * sortOrder;
}

function getFriendlyType(
  type: string | null | undefined,
  mimeType: string | null = null,
): FriendlyType | "Unknown" {
  if (isUserContent(type)) {
    if (isVideo(mimeType)) {
      return "Video";
    }

    if (isImage(mimeType)) {
      return "Image";
    }

    return "Unknown";
  }

  switch (type) {
    case MEDIA_ENTRY_APPLICATION:
      return "Application";
    case MEDIA_ENTRY_CONTENT_GROUP:
      return "Media Group";
    case MEDIA_ENTRY_FOLDER:
      return "Folder";
    case MEDIA_ENTRY_WEBPAGE:
      return "Web Page";
    default:
      return "Unknown";
  }
}

function strategyLevelAppToMediaEntry(
  stores: MediaStores,
  appId: string | number,
  index: number,
): MediaEntry {
  const { applications } = stores;

  const app = App.searchAllApps(applications, appId);

  return applicationToMediaEntry(app, index);
}

function strategyLevelWebpageToMediaEntry(
  webpage: {
    duration: number;
    name: string;
    url: string;
  },
  index: number,
): MediaEntry {
  const { duration, name, url } = webpage;

  return createMediaEntry({
    duration,
    name,
    url,
    _rowNum: index,
    friendlyType: getFriendlyType(MEDIA_ENTRY_WEBPAGE),
    type: MEDIA_ENTRY_WEBPAGE,
  });
}

type StrategyUserContentLike = {
  contentId: number;
  conflicts_with?: string | null;
  duration: number | null;
  id?: number | null;
};

function strategyLevelUCToMediaEntry(
  stores: MediaStores,
  strategyUC: StrategyUserContentLike,
  index: number,
): MediaEntry {
  const { userContents } = stores;
  const {
    id: rawId,
    contentId,
    conflicts_with: conflictsWith,
    duration: cgDuration,
  } = strategyUC;

  // We need to fix strategy level data structure to contain id.
  // In the mean time read either.
  const id = rawId ?? contentId;
  const storeUserContent = getById(userContents, id);

  const mediaEntry = userContentToMediaEntry(stores, storeUserContent, index);
  const { mimeType, duration: ucDuration } = mediaEntry;
  const duration = isVideo(mimeType) ? ucDuration : cgDuration;

  return {
    ...mediaEntry,
    conflictsWith: conflictsWith ? conflictsWith.split(",") : null,
    duration,
  };
}

type StrategyContentGroupLike = {
  contentGroupId: number;
  id?: number | null;
  options?: ReadonlyArray<{
    conflicts_with?: string | null;
    duration?: number | null;
    rrule?: string;
  }>;
};

function strategyLevelCGToMediaEntry(
  stores: MediaStores,
  stratCG: StrategyContentGroupLike,
  index: number,
): MediaEntry {
  const { contentGroups } = stores;
  const { id: rawId, contentGroupId, options = [] } = stratCG;

  // TODO: (akaringal/jhohmann) COR-2030: We need to fix strategy level
  // data structure to contain id. In the mean time read either.
  const id = rawId || contentGroupId;
  const storeContentGroup = getById(contentGroups, id);
  if (!storeContentGroup) {
    return createMediaEntry({
      _rowNum: index,
      friendlyType: getFriendlyType(MEDIA_ENTRY_CONTENT_GROUP),
      type: MEDIA_ENTRY_CONTENT_GROUP,
      id,
    });
  }
  const mediaEntry = contentGroupToMediaEntry(stores, storeContentGroup, index);
  const { content: storeContent } = mediaEntry;

  const content = (storeContent ?? []).map((userContentME, idx) => {
    const { duration: contentDuration, mimeType } = userContentME;
    const contentOptions = options[idx] || {};
    const { conflicts_with: conflictsWith = "", duration: strategyDuration } =
      contentOptions;
    const { duration: contentGroupDuration } = storeContentGroup.content[idx];

    const duration = isVideo(mimeType)
      ? contentDuration
      : strategyDuration || contentGroupDuration || contentDuration;
    return {
      ...userContentME,
      duration,
      conflictsWith: conflictsWith ? conflictsWith.split(",") : null,
    };
  });

  return {
    ...mediaEntry,
    content,
  };
}

function applicationToMediaEntry(
  storeApp: Application | null | undefined,
  index: number,
): MediaEntry {
  let entry: MediaEntryDraft = {
    _rowNum: index,
    friendlyType: getFriendlyType(MEDIA_ENTRY_APPLICATION),
    type: MEDIA_ENTRY_APPLICATION,
    account_id: null,
    archived: null,
    available: null,
    conflictsWith: null,
    content: null,
    content_groups: null,
    contents: null,
    create_date: null,
    displayName: null,
    duration: null,
    folder_id: null,
    folderName: null,
    id: null,
    installed: null,
    mimeType: null,
    name: null,
    numberOfSpots: null,
    tag: null,
    upload_date: null,
    url: null,
    user_id: null,
  };

  if (!storeApp) {
    return createMediaEntry(entry);
  }

  const { id } = storeApp;

  entry = {
    ...entry,
    id,
    displayName: App.name(storeApp),
    installed: App.installed(storeApp),
    name: App.name(storeApp),
    url: App.icon(storeApp),
    available: App.available(storeApp),
  };

  if (entry.installed) {
    const uploadDate = get(storeApp, "stable_version.change_date", null);
    Object.assign(entry, { upload_date: uploadDate });
  }

  return createMediaEntry(entry);
}

function contentFolderToMediaEntry(
  storeCF: MediaLibraryStoreContentFolder,
  index: number,
): MediaEntry {
  const {
    archived,
    content_groups,
    contents,
    create_date: uploadDate,
    id,
    name,
  } = storeCF;
  return createMediaEntry({
    archived,
    id,
    name,
    upload_date: uploadDate,
    _rowNum: index,
    // eslint-disable-next-line camelcase
    contents: [...contents, ...content_groups],
    displayName: name,
    friendlyType: getFriendlyType(MEDIA_ENTRY_FOLDER),
    type: MEDIA_ENTRY_FOLDER,
  });
}

function userContentToMediaEntry(
  stores: MediaStores,
  storeUC: ContentGroupItem | null | undefined,
  index: number,
): MediaEntry {
  const { contentFolders } = stores;
  if (!storeUC) {
    return createMediaEntry({
      _rowNum: index,
      friendlyType: getFriendlyType(MEDIA_ENTRY_USER_CONTENT),
      type: MEDIA_ENTRY_USER_CONTENT,
      account_id: null,
      archived: null,
      available: null,
      conflictsWith: null,
      content: null,
      content_groups: null,
      contents: null,
      create_date: null,
      displayName: null,
      duration: null,
      folder_id: null,
      folderName: null,
      id: null,
      installed: null,
      mimeType: null,
      name: null,
      numberOfSpots: null,
      tag: null,
      upload_date: null,
      url: null,
      user_id: null,
    });
  }
  const {
    account_id,
    archived,
    duration,
    folder_id,
    id,
    name,
    tag,
    type: mimeType,
    upload_date,
    url,
    user_id,
  } = storeUC;
  const folder = folder_id == null ? null : getById(contentFolders, folder_id);
  const folderName = folder ? folder.name : null;

  // Underscore to indicate its a special property that doesn't
  // really come from the model.
  const entry: MediaEntryDraft = {
    account_id,
    archived,
    duration,
    folder_id,
    folderName,
    id,
    mimeType,
    name,
    tag,
    upload_date,
    url,
    user_id,
    _rowNum: index,
    friendlyType: getFriendlyType(MEDIA_ENTRY_USER_CONTENT, mimeType),
    type: MEDIA_ENTRY_USER_CONTENT,
  };

  Object.assign(entry, {
    displayName: formatNameWithFolderName(name, entry),
  });

  return createMediaEntry(entry);
}

function contentGroupToMediaEntry(
  stores: MediaStores,
  storeCG: MediaLibraryStoreContentGroup | null | undefined,
  index: number,
): MediaEntry {
  const { contentFolders, userContents } = stores;
  if (!storeCG) {
    return createMediaEntry({
      _rowNum: index,
      friendlyType: getFriendlyType(MEDIA_ENTRY_CONTENT_GROUP),
      type: MEDIA_ENTRY_CONTENT_GROUP,
      account_id: null,
      archived: null,
      available: null,
      conflictsWith: null,
      content: null,
      content_groups: null,
      contents: null,
      create_date: null,
      displayName: null,
      duration: null,
      folder_id: null,
      folderName: null,
      id: null,
      installed: null,
      mimeType: null,
      name: null,
      numberOfSpots: null,
      tag: null,
      upload_date: null,
      url: null,
      user_id: null,
    });
  }

  const {
    account_id,
    archived,
    content: normContent,
    create_date: uploadDate,
    folder_id,
    id,
    name,
  } = storeCG;

  const folder = folder_id == null ? null : getById(contentFolders, folder_id);
  const folderName = folder ? folder.name : null;

  // We need to denormalize the content
  // content is a different shape than user content from strategy.
  const denormContent = normContent.map((cgUC, idx) => {
    const storeUC = cgUC.id == null ? null : getById(userContents, cgUC.id);
    const userContent = priorityMerge(
      {},
      storeUC || {},
      cgUC,
    ) as ContentGroupItem;

    return userContentToMediaEntry(stores, userContent, idx);
  });

  // Underscore to indicate its a special property that doesn't
  // really come from the model.
  const entry: MediaEntryDraft = {
    account_id,
    archived,
    folderName,
    folder_id,
    id,
    name,
    upload_date: uploadDate,
    _rowNum: index,
    friendlyType: getFriendlyType(MEDIA_ENTRY_CONTENT_GROUP),
    type: MEDIA_ENTRY_CONTENT_GROUP,
    content: denormContent,
    numberOfSpots: denormContent.length,
  };

  Object.assign(entry, {
    displayName: formatNameWithFolderName(name, entry),
  });

  return createMediaEntry(entry);
}

function getArchiveStatus(
  mediaEntry: Pick<MediaEntry, "archived" | "content">,
): typeof ARCHIVE_STATUS_ENTRY | typeof ARCHIVE_STATUS_CHILDREN | null {
  const { archived, content } = mediaEntry;

  if (archived) {
    return ARCHIVE_STATUS_ENTRY;
  }

  if (content && content.some((c) => c.archived)) {
    return ARCHIVE_STATUS_CHILDREN;
  }

  return null;
}

function webPageToMediaEntry(webPage: {
  duration: number | string;
  name: string;
  url: string;
}): MediaEntry {
  const { name, url, duration: durationInSecs } = webPage;
  const duration = Number(durationInSecs) * 1000;
  const webPageProps = {
    duration,
    name,
    url,
    friendlyType: getFriendlyType(MEDIA_ENTRY_WEBPAGE),
    type: MEDIA_ENTRY_WEBPAGE,
  };

  return createMediaEntry(webPageProps);
}

function folderContentsToSpots(
  applications: ApplicationsStore,
  userContents: MediaStoreSlice<ContentGroupItem>,
  contentGroups: MediaStoreSlice<MediaLibraryStoreContentGroup>,
  folder: MediaLibraryStoreContentFolder,
): MediaEntryList {
  const { content_groups: folderContentGroups, contents } = folder;

  const stores: MediaStores = {
    applications,
    userContents,
    contentGroups,
    contentFolders: { byId: {} },
  };

  const cgSpots = folderContentGroups
    .filter((cg) => !cg.archived)
    .map(contentGroupToMediaEntry.bind(null, stores));
  const contentSpots = contents
    .filter((c) => !c.archived)
    .map(userContentToMediaEntry.bind(null, stores));

  return [...cgSpots, ...contentSpots];
}

function normalizeContentGroup(mediaEntry: MediaEntry) {
  const { account_id, archived, content, upload_date, folder_id, id, name } =
    mediaEntry;

  const normalizedContent = (content ?? []).map((uc) => {
    const { id, duration, mimeType: type, name, url } = uc;

    return {
      id,
      duration,
      type,
      name,
      url,
    };
  });

  return {
    account_id,
    archived,
    content: normalizedContent,
    create_date: upload_date,
    folder_id,
    id,
    name,
  };
}

type GetTableDataArgs = {
  applications?: ApplicationsStore;
  contentFolders?: MediaStoreSlice<MediaLibraryStoreContentFolder>;
  contentGroups?: MediaStoreSlice<MediaLibraryStoreContentGroup>;
  includeArchived?: boolean;
  includeMediaTypes?: ReadonlyArray<string>;
  userContents?: MediaStoreSlice<ContentGroupItem>;
};

function getTableData({
  applications = { byId: {} },
  contentFolders = { byId: {} },
  contentGroups = { byId: {} },
  includeArchived = false,
  includeMediaTypes = [],
  userContents = { byId: {} },
}: GetTableDataArgs = {}): MediaEntryList {
  const includeApplication = includeMediaTypes.includes(
    MEDIA_ENTRY_APPLICATION,
  );
  const includeContentGroup = includeMediaTypes.includes(
    MEDIA_ENTRY_CONTENT_GROUP,
  );
  const includeFolder = includeMediaTypes.includes(MEDIA_ENTRY_FOLDER);
  const includeUserContent = includeMediaTypes.includes(
    MEDIA_ENTRY_USER_CONTENT,
  );

  const sources: [
    ReadonlyArray<Application>,
    ReadonlyArray<MediaLibraryStoreContentGroup>,
    ReadonlyArray<MediaLibraryStoreContentFolder>,
    ReadonlyArray<ContentGroupItem>,
  ] = [
    includeApplication ? getAllByIdAsArray(applications.byId || {}) : [],
    includeContentGroup ? getAllByIdAsArray(contentGroups.byId || {}) : [],
    includeFolder ? getAllByIdAsArray(contentFolders.byId || {}) : [],
    includeUserContent ? getAllByIdAsArray(userContents.byId || {}) : [],
  ];

  const stores: MediaStores = {
    applications,
    contentFolders,
    contentGroups,
    userContents,
  };

  const entries = getMediaEntries(stores, sources);
  return includeArchived
    ? entries
    : entries.filter(({ archived }) => !archived);
}

export {
  applicationToMediaEntry,
  contentFolderToMediaEntry,
  contentGroupToMediaEntry,
  createMediaEntry,
  folderContentsToSpots,
  formatNameWithFolderName,
  formatUploadDate,
  getArchiveStatus,
  getCreativeCategories,
  getFolderUrl,
  getFriendlyType,
  getMediaEntries,
  getTableData,
  isContentFolder,
  isImage,
  isUserContent,
  isValidMimeType,
  isVideo,
  MEDIA_ENTRY_SHAPE,
  normalizeContentGroup,
  sortByUploadDate,
  strategyLevelAppToMediaEntry,
  strategyLevelCGToMediaEntry,
  strategyLevelUCToMediaEntry,
  strategyLevelWebpageToMediaEntry,
  userContentToMediaEntry,
  webPageToMediaEntry,
};
